import { INTERVAL_SECONDS } from '../../config/timeframes';
import { safeNumber } from '../../lib/number';
import type { Candle, ChartInterval } from '../../types/market';
import type { KlineStreamState, KlineStreamStatus } from './binanceKlineStream';
import { OKX_INTERVAL } from './okx';

interface OkxKlinePayload {
  event?: string;
  code?: string;
  msg?: string;
  arg?: { channel?: string; instId?: string };
  data?: string[][];
}

interface SubscribeKlineOptions {
  onStatus?: (status: KlineStreamStatus) => void;
}

export function subscribeOkxKline(
  symbol: string,
  interval: ChartInterval,
  marketKind: 'spot' | 'futures',
  onUpdate: (update: { candle: Candle; eventTime: number }) => void,
  options: SubscribeKlineOptions = {},
): () => void {
  let stopped = false;
  let socket: WebSocket | undefined;
  let retryTimer: number | undefined;
  let retries = 0;
  let validEvents = 0;
  const endpoint = 'wss://ws.okx.com:8443/ws/v5/business';
  const baseAsset = symbol.endsWith('USDT') ? symbol.slice(0, -4) : symbol;
  const instId = marketKind === 'spot' ? `${baseAsset}-USDT` : `${baseAsset}-USDT-SWAP`;
  const channel = `candle${OKX_INTERVAL[interval]}`;

  const publish = (state: KlineStreamState, message: string): void => {
    options.onStatus?.({ state, message, retries, endpoint });
  };

  const scheduleReconnect = (): void => {
    if (stopped) return;
    retries += 1;
    if (validEvents === 0 && retries >= 6) {
      publish('unavailable', `OKX live ${interval} stream is unavailable after repeated attempts.`);
      return;
    }
    const delay = Math.min(20_000, 750 * 2 ** Math.min(retries, 5));
    publish('reconnecting', `OKX live ${interval} stream reconnecting in ${(delay / 1_000).toFixed(1)}s.`);
    retryTimer = window.setTimeout(connect, delay);
  };

  const connect = (): void => {
    if (stopped) return;
    publish(retries > 0 ? 'reconnecting' : 'connecting', `Connecting OKX live ${interval} candle stream.`);
    socket = new WebSocket(endpoint);
    const openTimeout = window.setTimeout(() => {
      if (socket?.readyState !== WebSocket.OPEN) socket?.close();
    }, 8_000);

    socket.addEventListener('open', () => {
      window.clearTimeout(openTimeout);
      socket?.send(JSON.stringify({ op: 'subscribe', args: [{ channel, instId }] }));
      publish('connecting', `OKX candle socket connected; awaiting the first verified ${interval} market event.`);
    });

    socket.addEventListener('message', (event) => {
      try {
        const payload = JSON.parse(String(event.data)) as OkxKlinePayload;
        if (payload.event === 'error' || (payload.code && payload.code !== '0')) {
          publish('reconnecting', `OKX rejected the candle subscription: ${payload.msg ?? payload.code ?? 'unknown error'}.`);
          socket?.close();
          return;
        }
        if (payload.arg?.channel !== channel || payload.arg?.instId !== instId) return;
        for (const row of payload.data ?? []) {
          const openTime = safeNumber(row[0]);
          const firstValidEvent = validEvents === 0;
          validEvents += 1;
          retries = 0;
          if (firstValidEvent) publish('live', `OKX live ${interval} candle events verified.`);
          onUpdate({
            candle: {
              openTime,
              closeTime: openTime + INTERVAL_SECONDS[interval] * 1_000 - 1,
              open: safeNumber(row[1]),
              high: safeNumber(row[2]),
              low: safeNumber(row[3]),
              close: safeNumber(row[4]),
              volume: safeNumber(row[5]),
              quoteVolume: safeNumber(row[7] ?? row[6]),
              closed: row[8] === '1',
            },
            eventTime: Date.now(),
          });
        }
      } catch (error) {
        publish(validEvents > 0 ? 'live' : 'connecting', `A malformed OKX candle event was ignored: ${error instanceof Error ? error.message : 'invalid payload'}.`);
      }
    });

    socket.addEventListener('close', () => {
      window.clearTimeout(openTimeout);
      if (!stopped) scheduleReconnect();
    });
    socket.addEventListener('error', () => socket?.close());
  };

  connect();
  return () => {
    stopped = true;
    if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    socket?.close();
  };
}
